import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { DetailField, PageHeader, PageLayout, SearchableSelect, SectionCard } from '../../components/ui';

const leaveTypes = [
  'Annual',
  'Sick',
  'Emergency',
  'Unpaid',
  'BabyBorn',
  'FamilyDeath',
  'Exam',
  'Haj',
  'Marriage',
  'Pregnancy',
  'HusbandDeath',
  'Payroll',
];

const initialForm = {
  user_id: '',
  leave_type: '',
  balance: 0,
};

const normalizeUsers = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const userLabel = (user) => {
  if (!user) return '';
  const name = user.name || user.full_name || user.username || 'Employee';
  const code = user.employee_code || user.id || '';
  const email = user.email ? ` · ${user.email}` : '';
  return `${name} (${code})${email}`;
};

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

function AddVacationBalancePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await api.get('/admin/users');
        setUsers(normalizeUsers(res.data));
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Pre-select the employee when arriving from "Add user" (and default to Annual,
  // the balance that is created at 0 days and needs setting for a new hire).
  useEffect(() => {
    const uid = searchParams.get('user_id');
    if (!uid) return;
    const fromNewUser = searchParams.get('from') === 'new-user';
    setForm((prev) => ({
      ...prev,
      user_id: uid,
      leave_type: fromNewUser && !prev.leave_type ? 'Annual' : prev.leave_type,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUser = useMemo(
    () => users.find((user) => String(user.employee_code) === String(form.user_id)) || null,
    [form.user_id, users]
  );

  const balanceValue = Number(form.balance);
  const requiredStatus = useMemo(
    () => [
      { label: 'Employee', done: Boolean(form.user_id) },
      { label: 'Leave type', done: Boolean(form.leave_type) },
      { label: 'Balance', done: Number.isFinite(balanceValue) && balanceValue >= 0 },
    ],
    [balanceValue, form.leave_type, form.user_id]
  );
  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleReset = () => {
    setError('');
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.post('/admin/vacation-balances', {
        user_id: form.user_id,
        leave_type: form.leave_type,
        balance: Number(form.balance),
      });
      toast.success('Vacation balance created successfully!');
      navigate('/admin/vacation-balances');
    } catch (err) {
      console.error('Failed to create vacation balance:', err);
      setError(err.response?.data?.message || 'Error creating vacation balance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Vacation balances"
        title="Add Vacation Balance"
        subtitle="Create a leave balance record for an employee and leave type."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/admin/vacation-balances')}
          >
            Back to Balances
          </Button>,
        ]}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          tone="dashboard"
          title="Balance Details"
          subheader="Select the employee, leave type, and opening balance."
          contentSx={{ p: { xs: 1.35, md: 1.7 } }}
        >
          <Stack spacing={1.8}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <FieldGroup
              icon={<PersonOutlineRoundedIcon color="primary" />}
              title="Employee"
              description="Use search when the employee list is long."
            >
              <SearchableSelect
                label="User"
                value={form.user_id}
                onChange={(value) => updateField('user_id', value)}
                options={users}
                loading={usersLoading}
                required
                getOptionLabel={userLabel}
                getOptionValue={(user) => user?.employee_code ?? ''}
                textFieldProps={{ sx: fieldSx }}
                noOptionsText={usersLoading ? 'Loading users...' : 'No users found'}
              />
            </FieldGroup>

            <Divider />

            <FieldGroup
              icon={<CalendarMonthRoundedIcon color="primary" />}
              title="Leave Type and Days"
              description="Enter the starting balance in days."
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.2,
                }}
              >
                <TextField
                  select
                  required
                  label="Leave Type"
                  value={form.leave_type}
                  onChange={(event) => updateField('leave_type', event.target.value)}
                  sx={fieldSx}
                >
                  {leaveTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  required
                  type="number"
                  label="Balance"
                  value={form.balance}
                  onChange={(event) => updateField('balance', event.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                  sx={fieldSx}
                />
              </Box>
            </FieldGroup>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button
                type="button"
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Balance'}
              </Button>
            </Stack>
          </Stack>
        </SectionCard>

        <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard
            tone="dashboard"
            title="Balance Summary"
            subheader="Preview before saving."
            contentSx={{ p: 1.6 }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={completionPct === 100 ? 'success' : 'warning'}
                  label={`${completionPct}% complete`}
                />
                <Chip size="small" variant="outlined" label="Create mode" />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              />
              <SummaryRow
                icon={<PersonOutlineRoundedIcon fontSize="small" />}
                label="Employee"
                value={selectedUser ? userLabel(selectedUser) : '-'}
              />
              <SummaryRow
                icon={<CalendarMonthRoundedIcon fontSize="small" />}
                label="Leave Type"
                value={form.leave_type}
              />
              <DetailField
                compact
                label="Balance"
                value={`${Number.isFinite(balanceValue) ? balanceValue : 0} day(s)`}
                icon={<TrendingUpRoundedIcon fontSize="small" />}
              />
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </PageLayout>
  );
}

export default AddVacationBalancePage;
